import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import PropertyCard from './card';
import imovel1 from '../../assets/imoveis/01.webp';
import imovel2 from '../../assets/imoveis/02.webp';
import imovel3 from '../../assets/imoveis/03.webp';
import { normalizeFavorites } from '../../utils/favorites';

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 40px 80px;

  @media (max-width: 1024px) {
    padding: 24px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  color: #222222;
  margin-bottom: 8px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #717171;
  margin-bottom: 32px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  align-items: stretch;
  justify-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GridItem = styled.div`
  width: 100%;
  min-width: 0;
`;

import useAuth from '../../hooks/useAuth';
import { useGlobalState } from '../../contexts/GlobalStateContext';

function App() {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]); // [{imovelFAVORITO, codigoFAVORITO, ...}]
  const { isLoggedIn, user } = useAuth();
  const globalState = useGlobalState();
  const handleOpenLogin = globalState?.handleOpenLogin || (() => {});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [compareItems, setCompareItems] = useState([]);
  const [compareDrawerOpen, setCompareDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const [paused, setPaused] = useState(false);
  const timerRef = React.useRef(null);

  // Carrega favoritos do usuário logado
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!isLoggedIn || !user) return;
      const token = localStorage.getItem('auth_token');
      try {
        const res = await fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${user.codigo || user.id || user.userId}`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        setFavorites(normalizeFavorites(data));
      } catch {
        setFavorites([]);
      }
    };
    fetchFavorites();
  }, [isLoggedIn, user]);

  // Função para favoritar
  const handleFavorite = async (property) => {
    if (!isLoggedIn) {
      handleOpenLogin();
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        imovelFAVORITO: property.id,
        usuarioFAVORITO: user?.codigo || user?.id || user?.userId,
        ativoFAVORITO: 1
      };
      console.log('Favoritar payload:', payload);
      const response = await fetch(`${import.meta.env.VITE_URL_API}/favoritos`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        // Atualize favoritos após favoritar
        const res = await fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${payload.usuarioFAVORITO}`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        setFavorites(normalizeFavorites(data));
        window.dispatchEvent(new Event('favoritesChanged'));
        try { Swal.fire({ toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'success', title: 'Adicionado aos favoritos' }); } catch {/* ignore */}
      } else {
        try { Swal.fire({ toast: true, position: 'top-end', timer: 2500, showConfirmButton: false, icon: 'error', title: 'Erro ao favoritar' }); } catch {/* ignore */}
      }
    } catch (err) {
      try { Swal.fire({ toast: true, position: 'top-end', timer: 2500, showConfirmButton: false, icon: 'error', title: 'Erro ao favoritar' }); } catch {/* ignore */}
    }
  };

  // Função para desfavoritar
  const handleUnfavorite = async (property) => {
    if (!isLoggedIn) {
      handleOpenLogin();
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      // Descubra o codigoFAVORITO pelo imovel
      const fav = favorites.find(f => String(f.imovelFAVORITO) === String(property.id));
      if (!fav) return;
      await fetch(`${import.meta.env.VITE_URL_API}/favoritos/${fav.codigoFAVORITO}`, {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });
      // Atualize favoritos após remover
      const res = await fetch(`${import.meta.env.VITE_URL_API}/favoritos/usuario/${user.codigo || user.id || user.userId}`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      setFavorites(normalizeFavorites(data));
      window.dispatchEvent(new Event('favoritesChanged'));
      try { Swal.fire({ toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'info', title: 'Removido dos favoritos' }); } catch {/* ignore */}
    } catch (err) {
      try { Swal.fire({ toast: true, position: 'top-end', timer: 2500, showConfirmButton: false, icon: 'error', title: 'Erro ao remover favorito' }); } catch {/* ignore */}
    }
  };

  // Helper: retorna true se o imóvel está favoritado
  const isFavorited = (propertyId) => {
    return favorites.some(fav => String(fav.imovelFAVORITO) === String(propertyId));
  };

  // Ações do Card
  const handleViewPhotos = (property) => {
    // Normaliza o array de fotos em URLs (aceita objetos {urlFOTO})
    const raw = Array.isArray(property.fotos) && property.fotos.length ? property.fotos : [imovel1, imovel2, imovel3];
    const imgs = raw.map(x => (typeof x === 'string' ? x : (x?.urlFOTO || ''))).filter(Boolean);
    let idx = 0;
    const renderHtml = (src, index, total) => `
      <div style="position:relative; display:flex; align-items:center; justify-content:center; background:#111; padding:0; margin:0;">
        <button id="lb-prev" style="position:absolute; left:12px; top:50%; transform:translateY(-50%); z-index:10; border:none; background:#fff; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; cursor:pointer;">❮</button>
        <img id="lb-img" src="${src}" style="max-width:90vw; max-height:80vh; object-fit:contain; border-radius:4px;" />
        <button id="lb-next" style="position:absolute; right:12px; top:50%; transform:translateY(-50%); z-index:10; border:none; background:#fff; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; cursor:pointer;">❯</button>
        <div id="lb-counter" style="position:absolute; bottom:12px; left:50%; transform:translateX(-50%); background:rgba(0,0,0,.6); color:#fff; padding:6px 10px; border-radius:12px; font-weight:600;">${index+1} / ${total}</div>
      </div>`;

    const update = (swalContent) => {
      const img = swalContent.querySelector('#lb-img');
      const counter = swalContent.querySelector('#lb-counter');
      if (img) img.src = imgs[idx];
      if (counter) counter.textContent = `${idx + 1} / ${imgs.length}`;
    };

    Swal.fire({
      html: renderHtml(imgs[idx], idx, imgs.length),
      width: '90%',
      padding: 0,
      background: '#111',
      showConfirmButton: false,
      showCloseButton: true,
      allowOutsideClick: false,
      didOpen: (el) => {
        const prev = el.querySelector('#lb-prev');
        const next = el.querySelector('#lb-next');
        const onPrev = () => { idx = (idx - 1 + imgs.length) % imgs.length; update(el); };
        const onNext = () => { idx = (idx + 1) % imgs.length; update(el); };
        prev?.addEventListener('click', onPrev);
        next?.addEventListener('click', onNext);
      }
    });
  };

  const handleViewVideo = () => {
    try {
      Swal.fire({
        title: 'Vídeo do imóvel',
        html: '<div style="padding:0;position:relative;"><iframe width="560" height="315" src="https://www.youtube.com/embed/Xw4LzUeyWEk?si=TRg4U_MKHkkCW1aN" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>',
        width: '80%',
        padding: '0',
        background: '#000',
        showConfirmButton: false,
        showCloseButton: true
      });
    } catch (_) {/* ignore */}
  };

  const handleCompare = (property) => {
    // Normaliza o item para o formato esperado pela página /compare
    const absolutize = (src) => {
      if (!src || typeof src !== 'string') return '';
      if (src.startsWith('http') || src.startsWith('data:')) return src;
      const cleaned = src.replace(/^\.*\/+/, '').replace(/^images\//, 'images/');
      const host = (import.meta.env.VITE_PUBLIC_ASSETS_HOST || '').replace(/\/$/, '');
      const base = host || String(import.meta.env.VITE_URL_API || '').replace(/\/api\/?$/, '');
      return `${base}/${cleaned}`;
    };
    const firstPhotoRaw = Array.isArray(property.fotos) && property.fotos.length
      ? (typeof property.fotos[0] === 'string' ? property.fotos[0] : (property.fotos[0]?.urlFOTO || ''))
      : imovel1;
    const firstPhoto = absolutize(firstPhotoRaw) || imovel1;
    const mapped = {
      id: property.id,
      title: property.tituloIMOVEL || 'Sem título',
      price: property.valorIMOVEL ?? '',
      location: property.enderecoIMOVEL || '',
      bedrooms: property.quartosIMOVEL ?? '',
      bathrooms: property.banheirosIMOVEL ?? '',
      area: property.metragemIMOVEL ?? '',
      garage: property.vagasgaragemIMOVEL ?? '',
      image: firstPhoto,
    };
    setCompareItems(prev => {
      const exists = prev.some(p => String(p.id) === String(mapped.id));
      const next = exists ? prev : [...prev, mapped];
      try { localStorage.setItem('compare_items', JSON.stringify(next)); } catch {/* ignore */}
      setCompareDrawerOpen(true);
      return next;
    });
  };

  const handleRemoveFromCompare = (id) => {
    setCompareItems(prev => {
      const next = prev.filter(p => String(p.id) !== String(id));
      try { localStorage.setItem('compare_items', JSON.stringify(next)); } catch {/* ignore */}
      return next;
    });
  };

  const handleClearCompare = () => {
    setCompareItems([]);
    try { localStorage.removeItem('compare_items'); } catch {/* ignore */}
  };

  const goToCompareNow = () => {
    if (compareItems.length < 2) {
      try { Swal.fire({ icon: 'info', title: 'Comparação', text: 'Selecione pelo menos 2 imóveis.' }); } catch (_) {/* ignore */}
      return;
    }
    navigate('/compare', { state: { items: compareItems } });
  };


  useEffect(() => {
    const loadFeatured = async () => {
      try {
        // Busca diretamente somente destaques ativos (público, sem Authorization)
        const res = await fetch(`${import.meta.env.VITE_URL_API}/imoveis?page=1&pageSize=10&destaque=1&ativo=1`, {
          headers: { accept: 'application/json' }
        });
        const json = await res.json().catch(() => ([]));
        // Suporta múltiplos formatos: array direto, {data: []} paginado, ou {dados: []}
        const lista = Array.isArray(json)
          ? json
          : (Array.isArray(json?.data)
              ? json.data
              : (Array.isArray(json?.dados) ? json.dados : []));

        // Mantém o array de fotos do backend (objetos com urlFOTO/pathFOTO/padraoFOTO)
        const adaptados = lista.map((item) => ({
          id: item.codigoIMOVEL || item.idIMOVEL || item.id,
          fotos: Array.isArray(item.fotos) ? item.fotos : [],
          tituloIMOVEL: item.tituloIMOVEL || 'Título não disponível',
          enderecoIMOVEL: `${item.cityIMOVEL || ''}${item.cityIMOVEL && item.stateIMOVEL ? ', ' : ''}${item.stateIMOVEL || ''}, ${item.countryIMOVEL || ''}` || 'Localização não disponível',
          valorIMOVEL: item.valorIMOVEL,
          quartosIMOVEL: item.quartosIMOVEL,
          banheirosIMOVEL: item.banheirosIMOVEL,
          metragemIMOVEL: item.metragemIMOVEL,
          tipometragemIMOVEL: item.tipometragemIMOVEL,
          vagasgaragemIMOVEL: item.vagasgaragemIMOVEL,
          tipoIMOVEL: item.tipoIMOVEL ?? item.tipo,
          labelvalorIMOVEL: item.labelvalorIMOVEL ?? item.labelvalor ?? item.labelValor,
          tag: 'DESTAQUE',
        }));

        const shuffled = adaptados
          .map(v => ({ v, sort: Math.random() }))
          .sort((a, b) => a.sort - b.sort)
          .map(({ v }) => v);

        setProperties(shuffled);
      } catch (e) {
        // Em caso de falha, deixa vazio mas sem quebrar
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };
    loadFeatured();
  }, []);

  // Rotaciona automaticamente quais 3 destaques são exibidos, com pausa em hover/interação
  useEffect(() => {
    if (properties.length === 0) return; // sem itens, não roda
    if (paused) return; // pausado por hover/interação
    if (timerRef.current) clearInterval(timerRef.current);
    // Intervalo mais lento para evitar "piscar" e permitir interação
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % properties.length);
    }, 7000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [properties.length, paused]);

  if (!loading && properties.length === 0) return null;

  return (
    <Container>
      <HeaderRow>
        <div>
          <Title>{t('featuredProperties.title')}</Title>
          <Subtitle>{t('featuredProperties.subtitle')}</Subtitle>
        </div>
        <button
          onClick={()=> navigate('/destaques')}
          style={{ border:'1px solid #e5e7eb', background:'#fff', color:'#374151', padding:'8px 12px', borderRadius:8, cursor:'pointer', whiteSpace:'nowrap' }}
        >
          {t('featuredProperties.viewAll')}
        </button>
      </HeaderRow>
      
      {loading ? (
        <div>{t('common.loading')}</div>
      ) : (
        <>
        <CardsGrid
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {(() => {
            const len = properties.length;
            if (len === 0) return null;
            // Sempre mostre 3 cards. Se houver menos itens, repete usando módulo.
            const a = properties[currentIndex % len];
            const b = properties[(currentIndex + 1) % len];
            const c = properties[(currentIndex + 2) % len];
            return [a, b, c].map((property) => (
              <GridItem key={`${property.id}-${Math.random().toString(36).slice(2,7)}`}>
                <PropertyCard
                  property={property}
                  onViewPhotos={handleViewPhotos}
                  onViewVideo={handleViewVideo}
                  onCompare={handleCompare}
                  isFavorited={isFavorited(property.id)}
                  onFavorite={handleFavorite}
                  onUnfavorite={handleUnfavorite}
                />
              </GridItem>
            ));
          })()}
        </CardsGrid>

        {/* Controles de navegação: Prev/Next + Bullets */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <button
            type="button"
            aria-label="Anterior"
            onClick={() => { setPaused(true); setCurrentIndex((p) => (p - 1 + properties.length) % properties.length); }}
            style={{ border: '1px solid #e5e7eb', background: '#fff', color: '#374151', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
          >
            ◀
          </button>

          <div style={{ display: 'flex', gap: 8 }}>
            {properties.map((_, i) => {
              const active = (i === (currentIndex % properties.length));
              return (
                <button
                  key={`dot-${i}`}
                  type="button"
                  aria-label={`Ir para item ${i+1}`}
                  onClick={() => { setPaused(true); setCurrentIndex(i); }}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    border: 'none',
                    background: active ? '#BA824F' : '#e5e7eb',
                    cursor: 'pointer'
                  }}
                />
              );
            })}
          </div>

          <button
            type="button"
            aria-label="Próximo"
            onClick={() => { setPaused(true); setCurrentIndex((p) => (p + 1) % properties.length); }}
            style={{ border: '1px solid #e5e7eb', background: '#fff', color: '#374151', padding: '6px 10px', borderRadius: 8, cursor: 'pointer' }}
          >
            ▶
          </button>
        </div>
        </>
      )}
      {/* Drawer de comparação */}
      {compareDrawerOpen && (
        <div style={{
          position: 'fixed', right: 90, bottom: 90, width: 340, maxHeight: '72vh',
          background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(8px)', border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: 16, boxShadow: '0 16px 40px rgba(0,0,0,.18)',
          padding: 18, zIndex: 12000, display: 'flex', flexDirection: 'column', gap: 14
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>{t('common.compareProperties')}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{compareItems.length}/4 {t('common.selected')}</div>
            </div>
            <button
              onClick={() => setCompareDrawerOpen(false)}
              style={{ border: 'none', background: '#f1f5f9', color: '#0f172a', borderRadius: 10, padding: '6px 8px', fontSize: 14, cursor: 'pointer' }}
            >✕</button>
          </div>
          <div style={{ overflowY: 'auto', paddingRight: 6, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {compareItems.map((p) => {
              const thumb = p.image || imovel1;
              return (
                <div key={`cmp-${p.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <img
                    src={thumb}
                    alt={p.title || p.tituloIMOVEL || 'Sem título'}
                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 10, background: '#e5e7eb', flexShrink: 0 }}
                    onError={(e)=>{ e.currentTarget.src = imovel1; e.currentTarget.onerror = null; }}
                    onLoad={(e)=>{
                      const img = e.currentTarget;
                      if (img.naturalWidth <= 2 && img.naturalHeight <= 2) {
                        img.src = imovel1;
                      }
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title || 'Sem título'}</div>
                    <div style={{ fontSize: 12, color: '#475569' }}>{p.price || ''}</div>
                  </div>
                  <button onClick={() => handleRemoveFromCompare(p.id)} title="Remover" style={{ border: 'none', background: 'transparent', fontSize: 16, cursor: 'pointer', color: '#c2410c' }}>✕</button>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleClearCompare} style={{ flex: 1, padding: '12px 14px', borderRadius: 12, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#0f172a', fontWeight: 600, cursor: 'pointer' }}>{t('common.clear')}</button>
            <button onClick={goToCompareNow} style={{ flex: 1, padding: '12px 14px', borderRadius: 12, border: 'none', background: '#BA824F', color: '#fff', fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 20px rgba(186,130,79,0.35)' }}>{t('common.compareNow')}</button>
          </div>
        </div>
      )}
    </Container>
  );
}

export default App;